import { z } from 'zod';

import { IntakeForm, ProviderIntakeForm } from '@/db/queries/intake-forms';
import { ContactTime } from '@/db/schema';

import quizJson from './quiz.json';

export const answerSchema = z.object({
  question: z.string(),
  text: z.string(),
  points: z.number(),
  key: z.string(),
});
export const quizStateSchema = z.object({
  answers: z.array(answerSchema),
  isValid: z.boolean(),
  hasPets: z.boolean(),
  points: z.number().default(0),
  created: z.string().default(new Date().toISOString()),
  submitted: z.string().optional(),
});

export type Answer = z.infer<typeof answerSchema>;
export type QuizState = z.infer<typeof quizStateSchema>;

export const quizInitialState: QuizState = {
  answers: [],
  isValid: false,
  hasPets: false,
  created: new Date().toISOString(),
  points: 0,
};

export function parseQuizState(quizStateUnparsed: unknown): QuizState {
  const quizStateParsed = quizStateSchema.safeParse(quizStateUnparsed);
  const quizState = quizStateParsed.success ? quizStateParsed.data : quizInitialState;

  return quizState;
}

type AnswerKey =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone_number'
  | 'preferred_contact_time'
  | 'preferred_contact_time_2'
  | 'state'
  | 'city'
  | 'terms_of_service';
type QuizAnswersLimited = Record<AnswerKey, string>;

export function getQuizAnswers(quiz: QuizState | { answers: QuizState['answers'] }) {
  const answers = quiz.answers.reduce<QuizAnswersLimited>((acc, answer) => {
    if (answer.key) {
      acc[answer.key as AnswerKey] = answer.text;
    }

    return acc;
  }, {} as QuizAnswersLimited);
  const diagnoses = quiz.answers.reduce<Record<string, number>>((acc, answer) => {
    const quizJsonAnswer = quizJson.find((q) => q.question === answer.question);
    if (quizJsonAnswer && quizJsonAnswer.diagnosis.length) {
      if (!acc[quizJsonAnswer.diagnosis]) {
        acc[quizJsonAnswer.diagnosis] = 0;
      }

      acc[quizJsonAnswer.diagnosis] += answer.points;
    }

    return acc;
  }, {});
  const formattedAnswers = {
    diagnoses,
    firstName: answers.first_name,
    lastName: answers.last_name,
    fullName: `${answers.first_name} ${answers.last_name}`,
    email: answers.email,
    phoneNumber: answers.phone_number,
    preferredContactTime: answers.preferred_contact_time as ContactTime,
    preferredContactTime2: (answers.preferred_contact_time_2 || null) as ContactTime | null,
    state: answers.state,
    city: answers.city || 'unspecified',
    termsOfService: answers.terms_of_service,
  };

  return formattedAnswers;
}

export type QuizKey =
  | 'happy'
  | 'stress'
  | 'sad'
  | 'suicide'
  | 'eighteen'
  | 'life_event'
  | 'lost_interest'
  | 'sad'
  | 'angry'
  | 'anxious'
  | 'sleep'
  | 'outbursts'
  | 'impulsive'
  | 'pain'
  | 'psychosis'
  | 'nervous'
  | 'paranoia'
  | 'compulsive_behaviors'
  | 'memory'
  | 'dissociation'
  | 'gender'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone_number'
  | 'preferred_contact_time'
  | 'preferred_contact_time_2'
  | 'state'
  | 'city'
  | 'referral'
  | 'terms_of_service';

export type QuizAnswers = Record<QuizKey, Answer>;

export function getQuizAnswersFromIntakeForm(intakeForm: ProviderIntakeForm | IntakeForm) {
  const answers = (intakeForm?.answers || {}) as QuizAnswers;

  return getQuizAnswers({ answers: Object.values(answers) as QuizState['answers'] });
}
